<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { CALENDAR_ROUTE_PERMISSIONS } from '../calendar/calendarAccess';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ContactContracts from './ContactContracts.vue';
import DayAgendaPanel from './DayAgendaPanel.vue';
import CommercialContextPanel from './CommercialContextPanel.vue';
import AiConversationPanel from './AiConversationPanel.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import AttendanceProtocolPanel from './AttendanceProtocolPanel.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ContactContractChooser from './ContactContractChooser.vue';
import QuickQuoteDialog from './QuickQuoteDialog.vue';
import ContactBillingDialog from './ContactBillingDialog.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const route = useRoute();
const router = useRouter();
const copy = {
  viewOnKanban: 'Ver no Kanban',
  simulateBanks: 'Simular nos bancos',
  sendContract: 'Enviar contrato',
  quickQuote: 'Orçamento rápido',
  loadingContracts: 'Carregando…',
  restaurantOrder: 'Novo pedido no Talkvia Food',
  contracts: 'Contratos',
  billing: 'Cobranças do contato',
  customerCenter: 'Central do cliente',
  unnamedCustomer: 'Cliente sem nome',
  crmSnapshot: 'Contexto rápido',
  protocol: 'Protocolo',
  cpf: 'CPF',
  collapseSnapshot: 'Recolher contexto rápido',
  expandSnapshot: 'Mostrar contexto rápido',
  noProtocol: 'Sem protocolo',
  noCpf: 'Sem CPF',
  copyValue: 'Copiar',
  copied: 'Copiado.',
};
const {
  uiSettings,
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);
const contactInfoRef = ref(null);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

// Mesmos perfis da rota da Agenda; a API aplica a visibilidade do Talkvia Studio.
const { checkPermissions } = usePolicy();
const canViewAgenda = computed(() =>
  checkPermissions(CALENDAR_ROUTE_PERMISSIONS)
);
const dayAgendaSummary = ref('');

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const hasRestaurantModule = computed(() =>
  isCloudFeatureEnabled('talkvia_restaurant')
);
const hasCorbanModule = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.TALKVIA_CORBAN)
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const quickQuoteRef = ref(null);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);
const attendanceProtocol = computed(
  () =>
    currentChat.value?.attendance_protocol ||
    conversationAdditionalAttributes.value.attendance_protocol ||
    ''
);
const attendanceProtocols = computed(
  () =>
    currentChat.value?.attendance_protocols ||
    conversationAdditionalAttributes.value.attendance_protocols ||
    []
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const createRestaurantOrder = () =>
  router.push({
    name: 'talkvia_restaurant_orders',
    params: { accountId: route.params.accountId },
    query: {
      new_order: '1',
      contact_id: contact.value.id,
      conversation_id: props.conversationId,
    },
  });

const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const crmContextCards = computed(() => [
  {
    key: 'protocol',
    icon: 'i-lucide-fingerprint',
    label: copy.protocol,
    value: attendanceProtocol.value || copy.noProtocol,
    muted: !attendanceProtocol.value,
  },
  {
    key: 'cpf',
    icon: 'i-lucide-id-card',
    label: copy.cpf,
    value: contact.value?.talkvia_profile?.cpf || copy.noCpf,
    muted: !contact.value?.talkvia_profile?.cpf,
  },
]);

// Só o campo preenchido ganha a ação de copiar; "Sem CPF" não é um valor.
const copyContextValue = async card => {
  if (card.muted) return;

  try {
    await copyTextToClipboard(String(card.value));
    useAlert(copy.copied, { variant: 'positive' });
  } catch (error) {
    // Área de transferência indisponível no navegador.
  }
};

const isCrmContextCollapsed = computed(
  () => !!uiSettings.value.is_contact_quick_context_collapsed
);

const toggleCrmContext = () => {
  updateUISettings({
    is_contact_quick_context_collapsed: !isCrmContextCollapsed.value,
  });
};

const openBankSimulation = () => {
  contactInfoRef.value?.openBankSimulationModal();
};

const openInKanban = () => {
  router.push({
    path: frontendURL(`accounts/${route.params.accountId}/kanban`),
    query: { conversation_id: conversationId.value },
  });
};

const openProtocolHistory = () => {
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId: contactId.value,
    },
    query: { tab: 'history' },
  });
};

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="contact-workspace w-full">
    <SidebarActionsHeader
      :title="copy.customerCenter"
      @close="closeContactPanel"
    />
    <div
      class="border-t border-n-weak bg-n-solid-2 px-3 pt-3"
      :class="isCrmContextCollapsed ? 'pb-1' : 'pb-2'"
    >
      <button
        type="button"
        class="flex w-full items-center justify-between rounded-md py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 transition hover:text-n-slate-12"
        :class="isCrmContextCollapsed ? 'mb-0' : 'mb-1.5'"
        :title="
          isCrmContextCollapsed ? copy.expandSnapshot : copy.collapseSnapshot
        "
        :aria-expanded="!isCrmContextCollapsed"
        @click="toggleCrmContext"
      >
        <span>{{ copy.crmSnapshot }}</span>
        <span
          class="i-lucide-chevron-down size-3.5 transition-transform"
          :class="isCrmContextCollapsed ? '-rotate-90' : 'rotate-0'"
        />
      </button>
      <!-- Protocolo e CPF ocupam uma faixa só: antes eram dois cartões empilhados, que
           consumiam altura no topo da coluna e repetiam a moldura do restante. -->
      <div v-if="!isCrmContextCollapsed" class="contact-workspace__snapshot">
        <div
          v-for="card in crmContextCards"
          :key="card.key"
          class="contact-workspace__snapshot-item"
        >
          <span :class="card.icon" class="size-3.5 shrink-0 text-n-slate-10" />
          <span class="min-w-0">
            <span
              class="block text-[10px] font-medium leading-3 text-n-slate-10"
            >
              {{ card.label }}
            </span>
            <span
              class="block truncate text-xs font-semibold leading-4"
              :class="card.muted ? 'text-n-slate-10' : 'text-n-slate-12'"
            >
              {{ card.value }}
            </span>
          </span>
          <button
            v-if="!card.muted"
            v-tooltip="`${copy.copyValue} ${card.label}`"
            type="button"
            class="ml-auto shrink-0 !p-0 text-n-slate-10 transition hover:text-n-slate-12"
            :aria-label="`${copy.copyValue} ${card.label}`"
            @click="copyContextValue(card)"
          >
            <span class="i-lucide-clipboard size-3.5" />
          </button>
        </div>
      </div>
    </div>

    <ContactInfo
      ref="contactInfoRef"
      :contact="contact"
      :channel-type="channelType"
      :enable-bank-simulation="hasCorbanModule"
    />
    <CommercialContextPanel
      v-if="hasCorbanModule"
      :conversation="currentChat"
      :contact="contact"
      :conversation-id="conversationId"
    />
    <AiConversationPanel :conversation="currentChat" />
    <div class="px-2 pb-3">
      <div class="grid grid-cols-2 gap-2">
        <ContactBillingDialog
          v-if="contact.id"
          v-slot="{ open, isLoading }"
          :contact="contact"
          :conversation-id="conversationId"
        >
          <button
            type="button"
            class="contact-workspace__action"
            :disabled="isLoading"
            @click="open"
          >
            <span class="inline-flex items-center gap-2">
              <span class="i-lucide-landmark size-4 text-n-brand" />
              {{ copy.billing }}
            </span>
            <span class="i-lucide-arrow-up-right size-4 text-n-slate-10" />
          </button>
        </ContactBillingDialog>
        <button
          v-if="hasCorbanModule"
          type="button"
          class="contact-workspace__action"
          @click="openBankSimulation"
        >
          <span class="inline-flex items-center gap-2">
            <span class="i-lucide-landmark size-4 text-n-brand" />
            {{ copy.simulateBanks }}
          </span>
          <span class="i-lucide-arrow-up-right size-4 text-n-slate-10" />
        </button>
        <button
          type="button"
          class="contact-workspace__action"
          @click="openInKanban"
        >
          <span class="inline-flex items-center gap-2">
            <span class="i-lucide-kanban-square size-4 text-n-brand" />
            {{ copy.viewOnKanban }}
          </span>
          <span class="i-lucide-arrow-up-right size-4 text-n-slate-10" />
        </button>
        <button
          v-if="hasRestaurantModule && contact.id"
          type="button"
          class="contact-workspace__action"
          @click="createRestaurantOrder"
        >
          <span class="inline-flex items-center gap-2">
            <span class="i-hugeicons-menu-restaurant size-4 text-n-brand" />
            {{ copy.restaurantOrder }}
          </span>
          <span class="i-hugeicons-arrow-up-right-01 size-4 text-n-slate-10" />
        </button>
        <ContactContractChooser
          v-if="contact.id"
          v-slot="{ open: openContractChooser, isLoading: isLoadingContracts }"
          :contact="contact"
          :conversation-id="conversationId"
          :inbox-id="currentChat.inbox_id"
        >
          <button
            type="button"
            class="contact-workspace__action"
            :disabled="isLoadingContracts"
            :aria-busy="isLoadingContracts"
            @click="openContractChooser"
          >
            <span class="inline-flex items-center gap-2">
              <span
                class="size-4 text-n-brand"
                :class="
                  isLoadingContracts
                    ? 'i-hugeicons-loading-03 animate-spin'
                    : 'i-lucide-file-signature'
                "
              />
              {{
                isLoadingContracts ? copy.loadingContracts : copy.sendContract
              }}
            </span>
            <span class="i-lucide-send size-4 text-n-slate-10" />
          </button>
        </ContactContractChooser>
        <button
          v-if="contact.id"
          type="button"
          class="contact-workspace__action"
          @click="quickQuoteRef?.open()"
        >
          <span class="inline-flex items-center gap-2">
            <span class="i-hugeicons-invoice-01 size-4 text-n-brand" />
            {{ copy.quickQuote }}
          </span>
          <span class="i-hugeicons-arrow-up-right-01 size-4 text-n-slate-10" />
        </button>
        <QuickQuoteDialog
          v-if="contact.id"
          ref="quickQuoteRef"
          :contact="contact"
          :conversation-id="conversationId"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
    </div>
    <div class="px-2 pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_info'"
            class="space-y-3"
          >
            <AccordionItem
              :title="$t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_attendance_protocol_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_attendance_protocol_open', value)
              "
            >
              <AttendanceProtocolPanel
                :conversation-id="conversationId"
                :protocol="attendanceProtocol"
                :protocols="attendanceProtocols"
                @view-full-history="openProtocolHistory"
              />
            </AccordionItem>
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_contracts'">
            <AccordionItem
              v-if="contact.id"
              :title="copy.contracts"
              :is-open="isContactSidebarItemOpen('is_contact_contracts_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_contracts_open', value)
              "
            >
              <ContactContracts :contact-id="contact.id" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'day_agenda'">
            <AccordionItem
              v-if="canViewAgenda"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.DAY_AGENDA')"
              :is-open="isContactSidebarItemOpen('is_day_agenda_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_day_agenda_open', value)
              "
            >
              <template #button>
                <span
                  v-if="dayAgendaSummary"
                  class="max-w-32 truncate text-xs font-medium tabular-nums text-n-slate-11"
                  :title="dayAgendaSummary"
                >
                  {{ dayAgendaSummary }}
                </span>
              </template>
              <DayAgendaPanel @summary="dayAgendaSummary = $event" />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'shared_files'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
              :is-open="isContactSidebarItemOpen('is_shared_files_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shared_files_open', value)
              "
            >
              <SharedFiles />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-workspace {
  @apply bg-n-solid-2;
}

.contact-workspace__snapshot {
  @apply grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-n-weak bg-n-weak;
}

.contact-workspace__snapshot-item {
  @apply flex min-w-0 items-center gap-2 bg-n-solid-1 px-2.5 py-1.5;
}

.contact-workspace__action {
  @apply flex w-full items-center justify-between gap-1 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 text-left text-xs font-medium text-n-slate-12 transition hover:border-n-brand hover:bg-n-alpha-2;

  /* O rótulo cabe em duas linhas curtas em vez de estourar a coluna. */
  span {
    @apply min-w-0;
  }
}

:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
